import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";

// Fix default marker icon URLs (Webpack breaks them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Cake-Pop-Talk branded pin
const brandIcon = L.divIcon({
  className: "cp-pin-editor",
  html: `<div style="width:34px;height:44px;position:relative;">
    <div style="position:absolute;top:0;left:0;width:34px;height:34px;background:#FF7E67;border:3px solid #ffffff;border-radius:50%;box-shadow:0 3px 10px rgba(0,0,0,0.35);"></div>
    <div style="position:absolute;top:26px;left:12px;width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:12px solid #FF7E67;"></div>
    <div style="position:absolute;top:9px;left:11px;width:12px;height:12px;background:#ffffff;border-radius:50%;"></div>
  </div>`,
  iconSize: [34, 44],
  iconAnchor: [17, 42],
});

function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && Array.isArray(center) && center[0] != null && center[1] != null) {
      map.setView(center, zoom || map.getZoom(), { animate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.[0], center?.[1]]);
  return null;
}

/**
 * Small map that lets a baker DRAG the pin to the exact spot for their address.
 * Emits { latitude, longitude } via onChange whenever the pin is dragged.
 */
export default function PinAdjuster({ latitude, longitude, onChange }) {
  const markerRef = useRef(null);

  const center = useMemo(() => {
    if (latitude != null && longitude != null) return [Number(latitude), Number(longitude)];
    return null;
  }, [latitude, longitude]);

  if (!center) {
    return (
      <div className="rounded-2xl border border-dashed border-[#EBDCD1] bg-[#F9EFE6]/40 p-6 text-center text-sm text-[#857163]" data-testid="pin-adjuster-empty">
        Enter your address above and save — once we've placed a pin, you can drag it here to fine-tune the exact spot.
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden border border-[#EBDCD1]" style={{ height: 320 }} data-testid="pin-adjuster">
      <MapContainer
        center={center}
        zoom={16}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Recenter center={center} zoom={16} />
        <Marker
          position={center}
          draggable
          icon={brandIcon}
          ref={markerRef}
          eventHandlers={{
            dragend: () => {
              const m = markerRef.current;
              if (!m) return;
              const ll = m.getLatLng();
              onChange && onChange({ latitude: ll.lat, longitude: ll.lng });
            },
          }}
        />
      </MapContainer>
    </div>
  );
}
